/**
 * Port 1:1 a JS de backend/research/rango_quantum/pine_indicators.py — que a
 * su vez es el port 1:1 del Pine original (docs/research/rango_quantum_pine_source.pine).
 *
 * REGLA DE ORO: cualquier cambio de fórmula aquí es un cambio de paridad —
 * se hace primero en pine_indicators.py (la referencia validada contra Pine,
 * 0.2 del estudio) y se replica aquí, nunca al revés. El test de paridad
 * cruzada (engine.parity.test.js) compara señales 1:1 contra el motor
 * Python sobre el MISMO dataset — si diverge, el build falla.
 *
 * Semánticas replicadas (idénticas a pine_indicators.py):
 * - ema(src, len): recursiva sin semilla SMA, alpha = 2/(len+1), arranca en
 *   el primer bar. Equivalente a pandas .ewm(span=len, adjust=False).
 * - rma(src, len): Wilder RMA — semilla SMA(len) sobre los primeros `len`
 *   valores VÁLIDOS (no NaN), luego rma[i] = (rma[i-1]*(len-1) + src[i]) / len.
 * - crossover/crossunder: comparación bar actual vs bar inmediatamente anterior.
 */

/** @param {number[]} values @param {number} length @returns {number[]} */
export function ema(values, length) {
  const n = values.length
  const out = new Array(n).fill(NaN)
  if (n === 0) return out
  const alpha = 2 / (length + 1)
  out[0] = values[0]
  for (let i = 1; i < n; i++) {
    out[i] = alpha * values[i] + (1 - alpha) * out[i - 1]
  }
  return out
}

/** Wilder RMA — tolera NaN líder (gain/loss de RSI arrancan en NaN por diff()). */
export function rma(values, length) {
  const n = values.length
  const out = new Array(n).fill(NaN)
  let validStart = 0
  while (validStart < n && Number.isNaN(values[validStart])) validStart++

  const seedEnd = validStart + length
  if (seedEnd <= n) {
    let sum = 0
    for (let i = validStart; i < seedEnd; i++) sum += values[i]
    out[seedEnd - 1] = sum / length
    for (let i = seedEnd; i < n; i++) {
      out[i] = (out[i - 1] * (length - 1) + values[i]) / length
    }
  }
  return out
}

/** @param {{high:number, low:number, close:number}[]} candles */
export function trueRange(candles) {
  const n = candles.length
  const out = new Array(n).fill(NaN)
  if (n === 0) return out
  out[0] = candles[0].high - candles[0].low
  for (let i = 1; i < n; i++) {
    const { high, low } = candles[i]
    const prevClose = candles[i - 1].close
    out[i] = Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose))
  }
  return out
}

export function atrWilder(candles, length = 14) {
  return rma(trueRange(candles), length)
}

export function rsiWilder(closes, length = 14) {
  const n = closes.length
  const gain = new Array(n).fill(NaN)
  const loss = new Array(n).fill(NaN)
  for (let i = 1; i < n; i++) {
    const delta = closes[i] - closes[i - 1]
    gain[i] = Math.max(delta, 0)
    loss[i] = Math.max(-delta, 0)
  }
  const avgGain = rma(gain, length)
  const avgLoss = rma(loss, length)

  const out = new Array(n).fill(NaN)
  for (let i = 0; i < n; i++) {
    const ag = avgGain[i]
    const al = avgLoss[i]
    if (Number.isNaN(ag) || Number.isNaN(al)) continue
    if (ag === 0 && al === 0) {
      out[i] = 50
    } else if (al === 0) {
      out[i] = 100
    } else {
      const rs = ag / al
      out[i] = 100 - 100 / (1 + rs)
    }
  }
  return out
}

export function macd(closes, fast = 12, slow = 26, signal = 9) {
  const emaFast = ema(closes, fast)
  const emaSlow = ema(closes, slow)
  const macdLine = emaFast.map((v, i) => v - emaSlow[i])
  const signalLine = ema(macdLine, signal)
  const hist = macdLine.map((v, i) => v - signalLine[i])
  return { macdLine, signalLine, hist }
}

export function sma(values, length) {
  const n = values.length
  const out = new Array(n).fill(NaN)
  let sum = 0
  for (let i = 0; i < n; i++) {
    sum += values[i]
    if (i >= length) sum -= values[i - length]
    if (i >= length - 1) out[i] = sum / length
  }
  return out
}

export function crossover(a, b) {
  const n = a.length
  const out = new Array(n).fill(false)
  for (let i = 1; i < n; i++) {
    out[i] = a[i] > b[i] && a[i - 1] <= b[i - 1]
  }
  return out
}

export function crossunder(a, b) {
  const n = a.length
  const out = new Array(n).fill(false)
  for (let i = 1; i < n; i++) {
    out[i] = a[i] < b[i] && a[i - 1] >= b[i - 1]
  }
  return out
}
